import { SortType } from '@zumma/ui'
import { ExpenseStatus, DownloadExpensesBulkType, ExpensesFilterBy, Expense } from './expenses'
import { Product, User, UserSettings } from './interfaces'
import { TaxInformation, InvoiceStatus } from './invoices'
import { AnalyticType } from './analytics'

export type ContributionsMonthByMonthResponse = Array<{
  month: number
  year: number
  total: number
}>

export type CardsIncomesResponse = Array<{
  totalIncome: number
  card: string
}>

export type CardsExpensesResponse = Array<{
  totalSpent: number
  card: string
}>

export type GetUserDetailsResponse = User

export type ExpensesVsCategoriesResponse = Array<{
  totalSpent: number
  expenseCategory: string
}>

export type PaymentBreakdownResponse = Array<{
  totalPercentage: number
  paymentMethodName: string
  totalSpent: number
}>

export type BudgetVsExpensesResponse = Array<{
  totalSpent: number
  budget: {
    name: string
    amount: number
  }
}>

export type TotalBalanceResponse = {
  total: number
}

export type BalanceByDateResponse = Array<{
  date: string
  total: number
}>

export enum OrgOrigin {
  VALERO = 'valero'
}

export function isOrganizationOrigin(origin: string): origin is OrgOrigin {
  return (Object.values(OrgOrigin) as string[]).includes(origin)
}

export interface SignUpBody {
  name?: string
  email: string
  password: string
  phone_number: string
  origin?: OrgOrigin | null
  captcha_token?: string
  request_org_access?: boolean
}

export interface SignInBody extends Omit<SignUpBody, 'phone_number'> {}

export type GetTaxInformationResponse = TaxInformation[]

export interface CreateTaxInformationBody extends Omit<TaxInformation, 'id' | 'tax_certificate' | 'tax_information_id' | 'created_at' | 'updated_at' | 'deleted_at'> {
  tax_certificate?: string | null
  email: string
}
export interface CreateTaxInformationResponse {
  data: TaxInformation
}

export interface UpdateTaxInformationBody extends Omit<TaxInformation, 'tax_certificate' | 'created_at' | 'updated_at' | 'deleted_at' | 'tax_information_id' | 'business_id'> {}
export interface UpdateTaxInformationResponse {
  data: TaxInformation
}
export interface DeleteTaxInformationResponse {
  data: TaxInformation
}
export interface EnableTaxInformationResponse {
  data: TaxInformation
}

export interface GetInvoicesStatusResponse {
  data: {
    active: boolean
    count: number
    limit: number
    total: number
  }
}

export interface GetInvoicesActiveResponse {
  data: {
    active: boolean
  }
}

export interface GetTaxInformationListResponse {
  data: TaxInformation[]
}

export interface GetTaxInformationByIdResponse {
  data: TaxInformation
}

export interface ResetPasswordBody {
  password: string
  access_token: string
  refresh_token: string
}

export interface VerifyRecoveryOtpBody {
  email: string
  token: string
  password: string
}

export interface VerifyRecoveryWhatsappBody {
  phone_number: string
  token: string
  password: string
}

/** Body of the self-service password change on the profile page. */
export interface ChangePasswordBody {
  current_password: string
  new_password: string
}

export enum PaymentType {
  BANK_TRANSFER = 'BANK_TRANSFER',
  STRIPE = 'STRIPE',
  FREE_TRIAL = 'FREE_TRIAL'
}
export interface Payment {
  amount: number
  type: PaymentType
  transfer_id?: string
  stripe_subscription_id?: string
}

export interface GetSubscriptionStatusResponse {
  data: {
    products: Array<Product>
    payment?: Payment
    business_available?: boolean
  }
}

export interface GetAllReceiptsResponse {
  data: Array<InvoiceStatus>
}

export interface GetReceiptResponse extends InvoiceStatus {}

export interface ScheduleInvoiceBody {
  tax_information_id: string
  file: string
  hook_url: string
  expense_id: number
  cfdi: string
  payment_method?: string | null
  metadata?: string | null
}

export interface ScheduleInvoiceResponse {
  data: {
    receipt_id: string
  }
  success: boolean
}

export interface BulkScheduleInvoiceFileBody {
  file: string
  metadata?: string | null
}

export interface BulkScheduleInvoicesBody {
  tax_information_id: number
  hook_url: string
  cfdi: string
  payment_method: string
  files: BulkScheduleInvoiceFileBody[]
}

export interface BulkScheduleInvoiceResult {
  success: boolean
  data: {
    receipt_id: number
    expense_id: number
  } | null
  error: string | null
  index: number
}

export interface BulkScheduleInvoicesResponse {
  success: boolean
}

export interface GetAllUsersResponse {
  data: User[]
  total: number
  total_enabled: number
}

export interface GetUserResponse {
  data: User
}

export interface CreateUserResponse {
  data: User
}

export interface UpdateUserResponse {
  data: User
}

export interface GetAllExpensesResponse {
  data: Array<ExpenseStatus>
  total: number
}

export interface DownloadExpensesDataBody {
  download_type: DownloadExpensesBulkType
  start_date: Date
  end_date: Date
}

export interface DeleteUserResponse {
  data: User
}

export interface EnableUserResponse {
  data: User
}

export interface GetAnalyticsBody {
  type: AnalyticType
  start_date: Date
  end_date: Date
  filter_by: ExpensesFilterBy
}

export interface GetAnalyticsResponse {
  data: { label: string; value: number }[]
}

export interface GetStatsResponse {
  data: {
    total_receipt_invoiced: number
    total_receipts: number
    total_expenses: number
    total_expenses_spent: number
    total_users: number
    total_businesses: number
    /** Non-archived ticket count: the displayed ticket count includes
     * archived, amounts don't, so amount-derived metrics need this
     * denominator. (total_expenses already excludes archived.) */
    total_receipts_active?: number
    /** Backend-computed Gasto Promedio (amounts without archived over
     * non-archived transactions). */
    average_expense?: number
  }
}

export interface GetReviewStatsResponse {
  data: {
    approved: number
    pending: number
    denied: number
  }
}

export interface GetInvoicingStatsResponse {
  data: {
    done: number
    pending: number
    error: number
  }
}

export interface UserInvoicingStats {
  user_id: number
  user_name: string
  done: number
  pending: number
  error: number
  total: number
  total_expenses: number
  total_expenses_spent: number
  total_receipts_spent: number
}

export interface GetUserInvoicingStatsResponse {
  data: UserInvoicingStats[]
  total: number
  page: number
  page_size: number
}

export interface UserExpensesStats {
  user_id: number
  user_name: string
  total_expenses: number
  total_spent: number
}

export interface GetUserExpensesStatsResponse {
  data: UserExpensesStats[]
  total: number
  page: number
  page_size: number
}

export interface TagExpensesStats {
  tag_id: number
  tag_name: string
  total_expenses: number
  total_spent: number
}

export interface GetTagExpensesStatsResponse {
  data: TagExpensesStats[]
  total: number
  page: number
  page_size: number
}

export interface TagInvoicingStats {
  tag_id: number
  tag_name: string
  done: number
  pending: number
  error: number
  total: number
  total_expenses_spent: number
  total_receipts_spent: number
}

export interface GetTagInvoicingStatsResponse {
  data: TagInvoicingStats[]
  total: number
  page: number
  page_size: number
}

export enum UserSortColumn {
  NAME = 'name',
  EMAIL = 'email',
  PHONE_NUMBERS = 'phone_numbers'
}

export interface GetAllUsersParams {
  filter_by?: string | undefined | null
  setStateResult?: boolean
  page?: number
  page_size?: number
  sort_by?: UserSortColumn
  sort_type?: SortType
}

export interface UpdateExpenseResponse {
  data: Expense
}

export interface CreateExpenseResponse {
  data: Expense
}

export interface SetUserSettingsBody {
  user_id: number
  payment_method?: string | null
  cfdi?: string | null
  default_reviewer_id?: number | null
  receive_reviewer_emails?: boolean
  download_field_labels?: Record<string, string> | null
}

export interface SetUserSettingsResponse {
  data: UserSettings | null
}

export interface GetUserSettingsResponse {
  data: UserSettings | null
}
